import { useEffect, useState } from "react";
import { Check, Copy, ExternalLink } from "lucide-react";
import { Button, Text } from "../../ui";
import { SettingsSection } from "./SettingsSection";
import { useUpdateWorkspace } from "../../../hooks/useWorkspace";
import { cn } from "../../../lib/cn";
import type { Workspace } from "../../../lib/api";
import { canUseCustomDomain, getPlatformKbDomain } from "../../../lib/plan-limits";
import { getPublicKbUrl } from "../../../lib/public-kb-host";

type PublicSiteSectionProps = {
  workspace: Workspace;
  isAdmin: boolean;
};

function getUrlSuffix() {
  if (import.meta.env.DEV) {
    return ".localhost:5173/knowledge";
  }
  return `.${getPlatformKbDomain()}/knowledge`;
}

export function PublicSiteSection({
  workspace,
  isAdmin,
}: PublicSiteSectionProps) {
  const [slug, setSlug] = useState(workspace.slug);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updateWorkspace = useUpdateWorkspace(workspace.id);

  const urlSuffix = getUrlSuffix();

  useEffect(() => {
    setSlug(workspace.slug);
  }, [workspace.slug]);

  const liveUrl = getPublicKbUrl(workspace.slug);
  const displayUrl = getPublicKbUrl(isAdmin ? slug : workspace.slug);

  async function handleCopy() {
    await navigator.clipboard.writeText(displayUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleSaveSlug() {
    setError(null);
    const result = await updateWorkspace.mutateAsync({ slug });
    if (!result.success) {
      setError(result.error.message);
    }
  }

  return (
    <SettingsSection
      id="public-site"
      title="Public site"
      description="Your customers can read published articles at this URL."
    >
      <div className="flex flex-col gap-4">
        <div>
          <Text variant="label" className="mb-1.5">
            Public URL
          </Text>
          <div className="flex flex-wrap items-center gap-2">
            <div
              className={cn(
                "flex min-h-11 md:min-w-0 flex-1 flex-wrap items-center gap-x-1 rounded-xl border bg-white px-3 py-2",
                error ? "border-red-500" : "border-foreground/20",
              )}
            >
              {isAdmin ? (
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  aria-label="Workspace slug"
                  className="max-w-16 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-foreground/40"
                />
              ) : (
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                  {workspace.slug}
                </span>
              )}
              <span className="shrink-0 text-sm text-foreground/50">
                {urlSuffix}
              </span>
            </div>

            <div className="flex shrink-0 gap-2">
              <Button
                variant="outline"
                size="sm"
                leftIcon={
                  copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )
                }
                onClick={handleCopy}
              >
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<ExternalLink className="h-4 w-4" />}
                onClick={() =>
                  window.open(liveUrl, "_blank", "noopener,noreferrer")
                }
              >
                Open
              </Button>
            </div>
          </div>

          {error ? (
            <Text variant="caption" className="mt-1.5 text-red-600">
              {error}
            </Text>
          ) : (
            isAdmin && (
              <Text variant="caption" className="mt-1.5">
                Changing the slug updates your public knowledge base URL.
              </Text>
            )
          )}

          {workspace.plan === "starter" && isAdmin && (
            <Text variant="caption" className="mt-2 text-foreground/50">
              Starter workspaces always show the &quot;Powered by SonderSoft&quot;
              footer on the public site. Upgrade to Growth to remove it.
            </Text>
          )}

          {canUseCustomDomain(workspace.plan) && isAdmin && (
            <Text variant="caption" className="mt-2 text-foreground/50">
              Enterprise workspaces can also map a custom domain (e.g.
              www.yourcompany.com). Contact us to set this up.
            </Text>
          )}
        </div>

        {isAdmin && (
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSaveSlug}
              loading={updateWorkspace.isPending}
              disabled={slug === workspace.slug}
            >
              Save slug
            </Button>
          </div>
        )}
      </div>
    </SettingsSection>
  );
}
